import React, { PureComponent, Fragment } from "react";
import moment from "moment";
import clsx from "clsx";
import Uri from "jsuri";

import {
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  Cell,
  ReferenceDot,
  Line,
  ComposedChart,
} from "recharts";

import missing_image from "./../../i/missing_image.svg";

import {
  Metric,
  MetricTable,
  selectFormat,
  MAIN_COLORS,
  CustomizedYAxisTick,
  StandardTooltip,
  Error,
  truncate,
  customColorIndex,
  formatDateFromData,
  isDateFromDataInISOFormat,
  getImageSrc,
  getAllKeys,
} from "components/Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  RenderBackground,
  InsightsTickIndicator,
  closeAllBarchartTooltips,
} from "components/Utils/InsightsUtils.js";

import Heading from "components/Elements/Heading";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

const metricBar = (cell) => (
  <MetricTable {...cell} graph={cell.trends} graphType="bar" />
);

class BarChartLegendsStacked extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;

    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );

      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  colorsIndex = () => new Uri(this.props.url).getQueryParamValue("colors");

  renderBars = (data) => {
    let bars = [];
    let labels = [];
    const sortedKeys = this.getSortedKeys(data);
    const insights = this.state.widgetsInsights;
    const isFixedBarWidth = data?.length <= 7;

    [...new Set(sortedKeys)].forEach((bar, i) => {
      let stack = "stack";
      if (bar !== "total subscriptions") {
        bars.push(
          <Line
            key={i}
            dataKey={bar}
            yAxisId="right"
            stroke={MAIN_COLORS[customColorIndex(i, this.colorsIndex())]}
            strokeWidth={2}
            dot={false}
            cursor="pointer"
            onClick={(d) => {
              const label = labels.filter((l) => l.date === d.date);
              const extra = sortedKeys
                .map((k, j) => ({
                  key: k,
                  value: d[k],
                  color: MAIN_COLORS[customColorIndex(j, this.colorsIndex())],
                }))
                .filter((o) => o.value !== undefined);
              d.extra = extra;
              d.insight = label.length ? label[0] : null;
              d.posts = d.posts || []; // TODO: get correct data from api
              d.topLists = this.props.data.topLists;
              this.clickBar(d, bar);
            }}
          ></Line>
        );
      } else {
        bars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId={stack}
            yAxisId="left"
            barSize={isFixedBarWidth ? 35 : null}
            fill={MAIN_COLORS[customColorIndex(i, this.colorsIndex())]}
            isAnimationActive={false}
            // only first bar needs background
            cursor="pointer"
            background={
              i === 0 ? (
                <RenderBackground
                  insights={insights}
                  showIndicator={!this.state.tooltip.show}
                  node={this.node}
                  i={i}
                />
              ) : null
            }
            onClick={(d) => {
              const label = labels.filter((l) => l.date === d.date);
              const extra = sortedKeys
                .map((k, j) => ({
                  key: k,
                  value: d[k],
                  color: MAIN_COLORS[customColorIndex(j, this.colorsIndex())],
                }))
                .filter((o) => o.value !== undefined);
              d.extra = extra;
              d.insight = label.length ? label[0] : null;
              d.posts = d.posts || []; // TODO: get correct data from api
              d.topLists = this.props.data.topLists;
              this.clickBar(d, bar);
            }}
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  ></Cell>
                )
            )}
          </Bar>
        );
      }
    });

    return bars;
  };

  clickBar(d, dataKey) {
    if (this.state.tooltip.show && d.date === this.state.tooltip.date) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="mt-2 legend mt-md-0">
        <span
          className="legend__icon"
          style={{
            background: MAIN_COLORS[customColorIndex(i, this.colorsIndex())],
          }}
        ></span>
        <span
          className="legend__text"
          style={{
            color: MAIN_COLORS[customColorIndex(i, this.colorsIndex())],
          }}
        >
          {entry}
        </span>
      </span>
    ));

  renderLegends = (data) => {
    const sortedKeys = this.getSortedKeys(data);
    return this.legendElement([...new Set(sortedKeys)]);
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);
    const keys = getAllKeys(data, ["date", "topContent", "total", "full_date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  getExportData = () => {
    let { data } = this.props.data;
    return {
      data: data,
      opts: {
        type: "chart",
        date: {
          // overwrites the default settings for date only when the date is in ISO format
          defined: isDateFromDataInISOFormat(this.props.data.data),
          format: "YYYY-MM-DDThh:mm:ssZ",
          viewFormat: "YYYY-MM-DD",
        },
      },
    };
  };

  render() {
    const { data, title, url } = this.props;
    const { widgetsInsights } = this.state;

    const widget_data = data.data;

    const insightsData = widgetsInsights.length > 0;

    const tenantsWithFullValues = ["lee"];
    const displayFullValues = tenantsWithFullValues.some((value) =>
      url.includes(value)
    );

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    const showFreeTrialStartsMetric = url.includes(
      "summary-board-free-trial-starts"
    );

    const showCancellationsMetric = url.includes("summary-board-cancellations");

    const showFreeTrialCancellationsMetric = url.includes(
      "summary-board-cancellations"
    );

    let preparedData = formatDateFromData(widget_data);

    return (
      <Fragment>
        <div ref={this.divPosition}>
          {this.renderLegends(preparedData)}
          {showFreeTrialStartsMetric && (
            <div className="my-2 d-block">
              <Heading size={6}>Free Trial Starts</Heading>
              <Metric {...data.free_trial_starts} size={"lg"} />
            </div>
          )}
          {showCancellationsMetric && showFreeTrialCancellationsMetric && (
            <div className="row">
              <div className="my-2 col">
                <Heading size={6}>Cancellations</Heading>
                <Metric {...data.cancellations} size={"lg"} />
              </div>

              <div className="my-2 col">
                <Heading size={6}>Free Trial Cancellations</Heading>
                <Metric {...data.ft_cancellations} size={"lg"} />
              </div>
            </div>
          )}

          <div className="mt-2 d-flex">
            <ResponsiveContainer height={370} width="100%">
              <ComposedChart
                data={preparedData}
                margin={{ top: 8, right: 0, bottom: 15, left: -38 }}
                stackOffset="sign"
                onMouseOver={(e) => CloseSelectedInsight(this.node)}
                onMouseOut={(e) =>
                  OpenSelectedInsight(this.node, !this.state.tooltip.show)
                }
              >
                <CartesianGrid
                  stroke="#FEE5DE"
                  strokeDasharray="3 3"
                  vertical={false}
                />

                {/* Adds insight icon under x-axis ticks  */}
                {insightsData &&
                  widgetsInsights.map((o, index) => (
                    <ReferenceDot
                      x={o.date}
                      y={0}
                      key={title + index}
                      ifOverflow="extendDomain"
                      shape={
                        <InsightsTickIndicator
                          insights={widgetsInsights}
                          date={o.date}
                          node={this.node}
                        />
                      }
                    />
                  ))}

                {!this.state.tooltip.show && (
                  <Tooltip
                    cursor={{ fill: "#eee", opacity: 0.5 }}
                    content={
                      <StandardTooltip
                        render={({ data }) => {
                          const payload = data(this.props);

                          if (payload !== null) {
                            const tooltip_data = payload[0].payload;

                            const topContent = tooltip_data.topContent;
                            let IMAGE_TOP_CONTENT = {
                              backgroundImage: `url(${missing_image})`,
                            };

                            if (topContent) {
                              if (topContent.thumbnail)
                                IMAGE_TOP_CONTENT = {
                                  backgroundImage: `url(${getImageSrc(
                                    topContent.thumbnail
                                  )})`,
                                };

                              topContent.value = topContent.views;
                              topContent.trends.forEach((chartData) => {
                                chartData.value = chartData.views;
                                chartData.label = chartData.date;
                                // eslint-disable-next-line no-self-assign
                                chartData.selected = chartData.selected;
                              });
                            }

                            const topList =
                              this.props.data.topLists &&
                              this.props.data.topLists.find(
                                (d) =>
                                  d.date.split("-").slice(1).join("/") ===
                                    tooltip_data.date ||
                                  moment(d.date).format("MM/YYYY") ===
                                    tooltip_data.date
                              ).topList;

                            if (topList) {
                              if (topList.thumbnail)
                                IMAGE_TOP_CONTENT = {
                                  backgroundImage: `url(${getImageSrc(
                                    topList.thumbnail
                                  )})`,
                                };

                              topList.value = topList.total;
                            }

                            return (
                              <div className="standard-tooltip">
                                {tooltip_data.date && (
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      Date:
                                    </span>
                                    <span className="standard-tooltip__value-header">
                                      {tooltip_data.date}
                                    </span>
                                  </header>
                                )}
                                <main className="standard-tooltip__main">
                                  {Object.keys(payload)
                                    .sort(
                                      (a, b) =>
                                        payload[b].value - payload[a].value
                                    )
                                    .map((key, index) => (
                                      <div
                                        className="standard-tooltip__item"
                                        key={key}
                                      >
                                        {payload[key].dataKey !== "insights" ? (
                                          <>
                                            <span
                                              className="standard-tooltip__icon"
                                              style={{
                                                background: payload[key].color,
                                              }}
                                            ></span>
                                            <span
                                              className={clsx(
                                                "standard-tooltip__key",
                                                "text-capitalize"
                                              )}
                                              style={{
                                                color: payload[key].color,
                                              }}
                                            >
                                              {payload[key].dataKey}{" "}
                                            </span>

                                            <span className="standard-tooltip__value">
                                              {selectFormat(
                                                "number",
                                                payload[key].value,
                                                displayFullValues
                                                  ? "0,00"
                                                  : null
                                              )}
                                            </span>
                                          </>
                                        ) : null}
                                      </div>
                                    ))}

                                  {widgetsInsights.length > 0 && (
                                    <InsightsWithinTooltip
                                      data={widgetsInsights}
                                      tooltipDate={tooltip_data.date}
                                    />
                                  )}

                                  {topContent && (
                                    <>
                                      <div className="mt-3 standard-tooltip__dilimiter2" />
                                      <div className="my-2 standard-tooltip__section-header">
                                        Top content on this day.
                                      </div>
                                      <div className="standard-tooltip__details--posts">
                                        <article className="content-table">
                                          <figure className="content-table__figure">
                                            <div
                                              className="content-table__img"
                                              style={IMAGE_TOP_CONTENT}
                                            />
                                          </figure>

                                          <section className="content-table__detail">
                                            <span className="standard-tooltip__value">
                                              {metricBar(topContent)}
                                            </span>
                                          </section>
                                        </article>

                                        <div className="standard-tooltip__text">
                                          {truncate(topContent.title, 200)}
                                        </div>
                                      </div>
                                    </>
                                  )}

                                  {topList && (
                                    <>
                                      <div className="mt-3 standard-tooltip__dilimiter2" />

                                      <div className="mt-3 mb-3 standard-tooltip__section-header">
                                        Top 5
                                      </div>
                                      {topList.map((item, i) => {
                                        return (
                                          <div
                                            key={i}
                                            className="mb-2 standard-tooltip__details--posts"
                                          >
                                            <article className="content-table">
                                              <figure className="content-table__figure">
                                                <div
                                                  className="content-table__img"
                                                  style={{
                                                    backgroundImage: `url(${getImageSrc(
                                                      item.thumbnail
                                                    )})`,
                                                  }}
                                                />
                                              </figure>

                                              <section className="content-table__detail">
                                                <span className="standard-tooltip__value">
                                                  {metricBar(item)}
                                                </span>
                                              </section>
                                            </article>

                                            <div className="standard-tooltip__text">
                                              {truncate(item.title, 200)}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </>
                                  )}
                                </main>
                              </div>
                            );
                          }
                        }}
                      />
                    }
                  />
                )}
                {this.renderBars(preparedData)}
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  scale="auto"
                />
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(d) => selectFormat("number", d)}
                  tick={<CustomizedYAxisTick />}
                />
                <YAxis
                  orientation="right"
                  yAxisId="right"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(d) => selectFormat("number", d)}
                  tick={<CustomizedYAxisTick />}
                />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="featured-insight-tooltip" ref={this.node}>
              {widgetsInsights.length > 0 && !this.state.tooltip.show && (
                <RenderTooltipInsights data={widgetsInsights} />
              )}
            </div>
          </div>

          <div
            className="xray-tooltip-container"
            style={{
              top: "5.4rem", //(this.state.tooltip.y - 20) + 'px',
              left:
                this.state.tooltip.x + this.state.tooltip.width + 220 <
                this.state.width
                  ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                  : this.state.tooltip.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              insights={widgetsInsights}
              close={this.closeTooltip.bind(this)}
            />
          </div>
        </div>
      </Fragment>
    );
  }
}
export default BarChartLegendsStacked;
